import { supabase } from "../lib/supabase";

export class ProjectEmailService {
  /**
   * Get all project emails
   * @returns {Promise<Array>} Array of project emails
   */
  static async getAllProjectEmails() {
    try {
      const { data, error } = await supabase
        .from('project_emails')
        .select(`
          *,
          projects (
            id,
            job_number,
            name,
            client_id
          )
        `)
        .not('project_id', 'is', null)  // Only show emails with a project assigned
        .neq('email_type', 'Admin Specific')  // Exclude admin-specific emails
        .order('received_at', { ascending: false });

      if (error) {
        console.error('Error fetching project emails:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllProjectEmails:', error);
      throw error;
    }
  }

  /**
   * Get a single project email by ID
   * @param {string} emailId - The UUID of the email
   * @returns {Promise<Object|null>} The email object or null if not found
   */
  static async getProjectEmailById(emailId) {
    try {
      const { data, error } = await supabase
        .from('project_emails')
        .select(`
          *,
          projects (
            id,
            job_number,
            name,
            client_id
          )
        `)
        .eq('id', emailId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Record not found
          return null;
        }
        console.error('Error fetching project email:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getProjectEmailById:', error);
      throw error;
    }
  }

  /**
   * Get emails filtered by project ID
   * @param {number} projectId - The project ID to filter by
   * @returns {Promise<Array>} Array of project emails for the specified project
   */
  static async getEmailsByProjectId(projectId) {
    try {
      const { data, error } = await supabase
        .from('project_emails')
        .select(`
          *,
          projects (
            id,
            job_number,
            name,
            client_id
          )
        `)
        .eq('project_id', projectId)
        .neq('email_type', 'Admin Specific')  // Exclude admin-specific emails
        .order('received_at', { ascending: false });

      if (error) {
        console.error('Error fetching emails by project:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getEmailsByProjectId:', error);
      throw error;
    }
  }

  /**
   * Create a new project email
   * @param {Object} emailData - The email data to create
   * @returns {Promise<Object>} The created email
   */
  static async createProjectEmail(emailData) {
    try {
      const { data, error } = await supabase
        .from('project_emails')
        .insert([emailData])
        .select(`
          *,
          projects (
            id,
            job_number,
            name,
            client_id
          )
        `)
        .single();

      if (error) {
        console.error('Error creating project email:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in createProjectEmail:', error);
      throw error;
    }
  }

  /**
   * Update a project email
   * @param {string} emailId - The UUID of the email to update
   * @param {Object} emailData - The updated email data
   * @returns {Promise<Object>} The updated email
   */
  static async updateProjectEmail(emailId, emailData) {
    try {
      const { data, error } = await supabase
        .from('project_emails')
        .update(emailData)
        .eq('id', emailId)
        .select(`
          *,
          projects (
            id,
            job_number,
            name,
            client_id
          )
        `)
        .single();

      if (error) {
        console.error('Error updating project email:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateProjectEmail:', error);
      throw error;
    }
  }

  /**
   * Delete a project email
   * @param {string} emailId - The UUID of the email to delete
   * @returns {Promise<boolean>} True if deleted successfully
   */
  static async deleteProjectEmail(emailId) {
    try {
      const { error } = await supabase
        .from('project_emails')
        .delete()
        .eq('id', emailId);

      if (error) {
        console.error('Error deleting project email:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteProjectEmail:', error);
      throw error;
    }
  }

  /**
   * Get email statistics
   * @returns {Promise<Object>} Object containing email statistics
   */
  static async getEmailStats() {
    try {
      const { data, error } = await supabase
        .from('project_emails')
        .select('email_type, project_id')
        .neq('email_type', 'Admin Specific')  // Exclude admin-specific emails
        .not('project_id', 'is', null);

      if (error) {
        console.error('Error fetching email stats:', error);
        throw error;
      }

      // Calculate statistics
      const stats = {
        total: data?.length || 0,
        byType: {},
        withProject: data?.filter(e => e.project_id).length || 0,
        withoutProject: data?.filter(e => !e.project_id).length || 0
      };

      // Group by email type (excluding Admin Specific which are already filtered out)
      data?.forEach(email => {
        const type = email.email_type || 'Unknown';
        stats.byType[type] = (stats.byType[type] || 0) + 1;
      });

      return stats;
    } catch (error) {
      console.error('Error in getEmailStats:', error);
      throw error;
    }
  }
}